import { useState, useEffect } from 'react';
import {
  AccountType,
  ACCOUNT_TYPE_LABELS,
  ACCOUNT_TYPE_COLORS,
  CreateAccountRequest,
  Account,
} from '../../types/account';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { getCurrencyCode } from '../../lib/currency';
import { useTranslation } from 'react-i18next';

interface AccountFormProps {
  onSubmit: (data: CreateAccountRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
  initialData?: Account;
  mode?: 'create' | 'edit';
}

export function AccountForm({ onSubmit, onCancel, isLoading, initialData, mode = 'create' }: AccountFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialData?.name || '');
  const [type, setType] = useState<AccountType>(initialData?.type || 'bank');
  const [balance, setBalance] = useState(initialData?.balance.toString() || '0');
  const [description, setDescription] = useState(initialData?.description || '');
  const [color, setColor] = useState(initialData?.color || ACCOUNT_TYPE_COLORS['bank']);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setType(initialData.type);
      setBalance(initialData.balance.toString());
      setDescription(initialData.description || '');
      setColor(initialData.color || ACCOUNT_TYPE_COLORS[initialData.type]);
    }
  }, [initialData]);

  const handleTypeChange = (newType: AccountType) => {
    setType(newType);
    setColor(ACCOUNT_TYPE_COLORS[newType]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    onSubmit({
      name,
      type,
      balance: parseFloat(balance) || 0,
      currency: getCurrencyCode(),
      description: description || undefined,
      color,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label={t('accounts:account_name')}
        name="name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('accounts:account_name_placeholder')}
        required
      />

      {mode === 'create' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('accounts:account_type')}
            </label>
            <select
              name="type"
              value={type}
              onChange={(e) => handleTypeChange(e.target.value as AccountType)}
              className="input"
              required
            >
              {Object.keys(ACCOUNT_TYPE_LABELS).map((value) => (
                <option key={value} value={value}>
                  {t(`accounts:types.${value}`)}
                </option>
              ))}
            </select>
          </div>

          <Input
            label={t('accounts:initial_balance')}
            name="initial_balance"
            type="number"
            value={balance}
            onChange={(e) => setBalance(e.target.value)}
            placeholder="0"
            step="0.01"
          />
        </>
      )}

      <Input
        label={t('accounts:account_description')}
        name="description"
        type="text"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={t('accounts:account_description_placeholder')}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('accounts:account_color')}
        </label>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
          />
          <span className="text-sm text-gray-600">{color}</span>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={isLoading} className="flex-1">
          {isLoading
            ? (mode === 'edit' ? t('accounts:updating') : t('accounts:creating'))
            : (mode === 'edit' ? t('accounts:update_account') : t('accounts:create_account'))
          }
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t('common:cancel')}
        </Button>
      </div>
    </form>
  );
}
